// React
import { useCallback, useEffect, useMemo, useState } from "react";

// Helpers
import { formatCurrency } from "helpers";

// Servisni sloj
import { initialOverrideReport } from "services/api/pricing/consts";
import { getEffectivePricingVersionAPI, getPricingPacksAPI } from "services/api/pricing/api";
import { buildPricingKeyOptions } from "services/api/pricing/keyOptions";
import { PricingKeyOption } from "services/api/pricing/types";
import {
  approvePricingOverrideAPI,
  getAllPricingOverridesAPI,
  rejectPricingOverrideAPI,
} from "services/api/pricing/overrideApi";
import {
  DeviationFilter,
  PricingOverride,
  PricingOverrideReport,
} from "services/api/pricing/overrideTypes";
import { searchTenantsAPI } from "services/api/tenants/api";
import { TenantRow } from "services/api/tenants/types";
import { initialDataWithPagination } from "services/api/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Button } from "ui/components/Button/Button";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { Loader } from "ui/components/Loader";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { PRICING_DEVIATIONS_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";
import PricingTabMenu from "ui/modules/pricing/components/PricingTabMenu/PricingTabMenu";

// Lokalne komponente
import DeviationTable from "./components/DeviationTable/DeviationTable";
import PricingOverrideEditCard from "ui/modules/pricing/components/PricingOverrideEditCard/PricingOverrideEditCard";

// Tipovi
import {
  DeviationRow,
  PricingDeviationsPageCmp,
  PricingDeviationsPageProps,
} from "./types";

const FILTER_OPTIONS: DropdownOption[] = [
  { value: "all", text: "Sve" },
  { value: "effective", text: "Aktivno" },
  { value: "pending", text: "Čeka odobrenje" },
  { value: "expiring", text: "Istječe u 30 dana" },
  { value: "expired", text: "Isteklo" },
];

const matchesFilter = (row: DeviationRow, filter: DeviationFilter): boolean => {
  switch (filter) {
    case "effective":
      return row.state === "Effective";
    case "pending":
      return row.state === "PendingApproval";
    case "expiring":
      return row.state === "Effective" && row.expiresInDays >= 0 && row.expiresInDays <= 30;
    case "expired":
      return row.state === "Expired";
    default:
      return true;
  }
};

/**
 * Izvještaj o odstupanjima — sva pregazivanja na jednom mjestu, i uređivanje po klijentu.
 *
 * Bez ovog ekrana nitko ne zna što se daje.
 */
const PricingDeviationsPage: PricingDeviationsPageCmp = (_props: PricingDeviationsPageProps) => {
  const title = PRICING_DEVIATIONS_PAGE_TITLE;

  const [report, setReport] = useState<PricingOverrideReport>(initialOverrideReport);
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [keyOptions, setKeyOptions] = useState<PricingKeyOption[]>([]);
  const [filter, setFilter] = useState<DeviationFilter>("all");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [editOverride, setEditOverride] = useState<PricingOverride | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    fetchInitial();
  }, []);

  const fetchInitial = async () => {
    setIsLoading(true);

    try {
      const [all, tenantPage, version, packs] = await Promise.all([
        getAllPricingOverridesAPI(),
        searchTenantsAPI({ ...initialDataWithPagination.pagination, pageSize: 200 }),
        getEffectivePricingVersionAPI(),
        getPricingPacksAPI(),
      ]);

      setReport(all);
      setTenants(tenantPage.data);

      // Packovi su ovdje samo zato što šifra kombinacije bez njihovih naziva ništa ne govori.
      // Nabavne cijene se ne nude — brana je i na backendu.
      setKeyOptions(buildPricingKeyOptions(version, packs));
    } catch {
      // greška se prikazuje kroz snackbar
    } finally {
      setIsLoading(false);
    }
  };

  const rows: DeviationRow[] = useMemo(() => {
    const nameById = new Map(
      tenants.map((tenant) => [tenant.id, tenant.name ?? tenant.companyName ?? tenant.id])
    );

    return report.overrides
      .map((item) => ({
        ...item,
        tenantName: nameById.get(item.tenantId) ?? item.tenantId,
      }))
      .filter((row) => matchesFilter(row, filter));
  }, [report.overrides, tenants, filter]);

  const handleFilterChange = useCallback((_e: any, _name: string, item: DropdownOption) => {
    setFilter(item.value as DeviationFilter);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setReport(await getAllPricingOverridesAPI());
    } catch {
      // greška se prikazuje kroz snackbar
    }
  }, []);

  const handleApprove = useCallback(
    async (row: DeviationRow) => {
      try {
        await approvePricingOverrideAPI(row.id);
        await refresh();
      } catch {
        // greška se prikazuje kroz snackbar
      }
    },
    [refresh]
  );

  const handleReject = useCallback(
    async (row: DeviationRow) => {
      // Razlog je obavezan; bez njega backend odbija.
      const reason = window.prompt("Razlog odbijanja:");

      if (!reason) {
        return;
      }

      try {
        await rejectPricingOverrideAPI(row.id, reason);
        await refresh();
      } catch {
        // greška se prikazuje kroz snackbar
      }
    },
    [refresh]
  );

  const handleAddClick = useCallback(() => {
    setEditOverride(null);
    setShowEditModal(true);
  }, []);

  const handleEdit = useCallback((row: DeviationRow) => {
    setEditOverride(row);
    setShowEditModal(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
    setEditOverride(null);
  }, []);

  const handleEditSuccess = useCallback(async () => {
    setShowEditModal(false);
    setEditOverride(null);
    await refresh();
  }, [refresh]);

  const pendingCount = report.overrides.filter((item) => item.state === "PendingApproval").length;

  return (
    <div>
      <div className="bg-white rounded-t-md">
        <PageHeader>
          <div className="flex items-center justify-between">
            <div>
              <PageTitle title={title.text} icon={title.icon} iconColor={title.iconColor} />
              <Breadcrumb />
            </div>

            <Button
              variant="primary"
              children="Novi poseban uvjet"
              onClick={handleAddClick}
              startIcon="/media/icons/plus.svg"
            />
          </div>
        </PageHeader>
        <PricingTabMenu />
      </div>

      {/* Jedna ploha, sekcije razdvojene crtom — ne niz zasebnih bijelih kartica. */}
      <div className="divide-y divide-slate-200">
        <div className="p-6 flex flex-col md:flex-row md:items-end gap-4">
          <div className="w-full md:w-[260px]">
            <Dropdown
              name="filter"
              label="Status"
              value={filter}
              options={FILTER_OPTIONS}
              onChange={handleFilterChange}
              width="full"
            />
          </div>

          <div className="flex-1 flex flex-wrap items-center gap-6 pb-1">
            <div>
              <p className="text-xs text-slate-500">Prikazano</p>
              <p className="text-xl font-bold text-slate-700">{rows.length}</p>
            </div>

            <div>
              <p className="text-xs text-slate-500">Čeka odobrenje</p>
              <p className="text-xl font-bold text-slate-700">{pendingCount}</p>
            </div>

            <div>
              <p className="text-xs text-slate-500">Mjesečni učinak</p>
              <p className="text-xl font-bold text-slate-700">
                {formatCurrency.format(report.measuredMonthlyEffect)}
              </p>
            </div>
          </div>
        </div>

        <DeviationTable
          rows={rows}
          onApprove={handleApprove}
          onReject={handleReject}
          onEdit={handleEdit}
        />

        <div className="p-6">
          <p className="text-xs text-slate-500">
            Mjesečni učinak mjeri koliko nas poseban uvjet stoji u odnosu na cijenu nakon grupe.
            Učinak same grupe ovdje nije uračunat. On pripada izvještaju o grupama, a zbrojen i
            ovdje bio bi izbrojen dvaput. Za ono što čeka odobrenje prikazan je iznos koji bi
            vrijedio kad bi bilo odobreno.
          </p>

          {report.unmeasuredKeys.length > 0 && (
            <p className="text-xs text-slate-500 mt-2">
              Zbroj ne obuhvaća{" "}
              <span className="font-mono">{report.unmeasuredKeys.join(", ")}</span>. Količina se
              za te ključeve ne mjeri, pa bi nula lagala prema dolje. U tablici stoje kao „—".
            </p>
          )}
        </div>
      </div>

      <PricingOverrideEditCard
        show={showEditModal}
        override={editOverride}
        tenants={tenants}
        keyOptions={keyOptions}
        onSuccess={handleEditSuccess}
        onClose={handleEditClose}
      />

      <Loader isLoading={isLoading} message="Učitavanje podataka…">
        {null}
      </Loader>
    </div>
  );
};

PricingDeviationsPage.route = Routes.PricingDeviationsPageRouter;

export default PricingDeviationsPage;
